import { Search, Trash2 } from "lucide-react";
import { useRef } from "react";

import { Button } from "../../components/ui/Button";

interface TargetsToolbarProps {
  count: number;
  query: string;
  onQuery: (q: string) => void;
  onImport: (file: File) => void;
  onExport: () => void;
  onAdd: () => void;
  ungrouped: boolean;
  onToggleUngrouped: () => void;
  selectedCount: number;
  onDeleteSelected: () => void;
}

export function TargetsToolbar({
  count,
  query,
  onQuery,
  onImport,
  onExport,
  onAdd,
  ungrouped,
  onToggleUngrouped,
  selectedCount,
  onDeleteSelected,
}: TargetsToolbarProps) {
  const fileInputRef = useRef<HTMLInputElement | null>(null);

  return (
    <div style={{ display: "flex", alignItems: "center", gap: 10, flexWrap: "wrap" }}>
      <h3 style={{ margin: 0, fontSize: 15, fontWeight: 700, color: "var(--text-primary)" }}>
        Targets
      </h3>
      <span style={{ color: "var(--text-muted)", fontFamily: "var(--font-mono)", fontSize: 11 }}>
        · {count} registered
      </span>

      <div style={{ position: "relative", flex: 1, minWidth: 180 }}>
        <Search
          size={13}
          style={{
            position: "absolute",
            left: 10,
            top: "50%",
            transform: "translateY(-50%)",
            color: "var(--text-muted)",
            pointerEvents: "none",
          }}
        />
        <input
          value={query}
          onChange={(e) => onQuery(e.target.value)}
          placeholder="search name / phone / email…"
          style={{
            width: "100%",
            boxSizing: "border-box",
            background: "var(--bg-input)",
            border: "1px solid var(--border)",
            borderRadius: 8,
            padding: "7px 12px 7px 30px",
            color: "var(--text-primary)",
            fontFamily: "var(--font-mono)",
            fontSize: 11.5,
            outline: "none",
          }}
        />
      </div>

      <Button
        variant="secondary"
        onClick={onToggleUngrouped}
        style={{
          ...legacySecondaryBtn,
          border: ungrouped ? "1px solid var(--accent)" : "1px solid var(--border)",
          background: ungrouped ? "var(--accent-subtle)" : "var(--bg-tertiary)",
          color: ungrouped ? "var(--accent)" : "var(--text-secondary)",
        }}
      >
        {ungrouped ? "✓ Ungrouped" : "Ungrouped"}
      </Button>

      {selectedCount > 0 && (
        <Button
          variant="secondary"
          onClick={onDeleteSelected}
          style={{
            ...legacySecondaryBtn,
            border: "1px solid var(--error)",
            background: "transparent",
            color: "var(--error)",
            display: "inline-flex",
            alignItems: "center",
            gap: 6,
          }}
        >
          <Trash2 size={13} /> Delete {selectedCount} selected
        </Button>
      )}

      <Button
        variant="secondary"
        onClick={() => fileInputRef.current?.click()}
        style={legacySecondaryBtn}
      >
        ↑ import CSV
      </Button>
      <Button variant="secondary" onClick={onExport} style={legacySecondaryBtn}>
        ↓ export CSV
      </Button>
      <Button
        variant="primary"
        onClick={onAdd}
        style={{
          ...legacySecondaryBtn,
          border: "1px solid var(--accent)",
          background: "var(--accent)",
          color: "white",
        }}
      >
        + new target
      </Button>

      <input
        ref={fileInputRef}
        type="file"
        accept=".csv,text/csv"
        style={{ display: "none" }}
        onChange={(e) => {
          const file = e.target.files?.[0];
          if (file) onImport(file);
          e.target.value = "";
        }}
      />
    </div>
  );
}

// Boardroom secondary-button recipe, passed through <Button> as a style override
// so the look is unchanged; <Button> adds the mobile --tap-min floor on top.
const legacySecondaryBtn: React.CSSProperties = {
  padding: "7px 12px",
  borderRadius: 8,
  background: "var(--bg-tertiary)",
  border: "1px solid var(--border)",
  color: "var(--text-secondary)",
  fontSize: 11,
  fontWeight: 600,
  whiteSpace: "nowrap",
};
